import { Tooltip } from 'antd'
import { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import { BonusStat } from '@/api/type-response'
import InfoIcon from '@/assets/icons/info-circle.svg?react'
import { Divider } from '@/components/base/Divider'
import { Trans } from '@/components/Trans.tsx'
import { formatPrice } from '@/utils/formatter.ts'
import { cn } from '@/utils/style'

// per design: centered 10.5px/15px semibold text in a 135px-wide bubble
const tipOverlayStyle: CSSProperties = { maxWidth: 135 }
const tipInnerStyle: CSSProperties = {
  textAlign: 'center',
  fontSize: 10.5,
  lineHeight: '15px',
  fontWeight: 600,
  padding: 6,
  minHeight: 0,
  borderRadius: 8,
}

// sm: header dropdown (12px), md: balance detail modal (14px)
type BreakdownSize = 'sm' | 'md'

interface BreakdownRowProps {
  i18nKey: string
  defaultLabel: string
  tipKey?: string
  moneyCent?: number
  isBalanceHidden?: boolean
  size: BreakdownSize
}

const BreakdownRow = ({
  i18nKey,
  defaultLabel,
  tipKey,
  moneyCent,
  isBalanceHidden,
  size,
}: BreakdownRowProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn('w-full flex items-center justify-between', size === 'md' && 'py-3')}>
      <div className="flex items-center gap-1">
        <div className={cn(size === 'sm' ? 't-xs' : 't-sb', 'text-secondary')}>
          <Trans i18nKey={i18nKey}>{defaultLabel}</Trans>
        </div>
        {tipKey && (
          <Tooltip
            title={t(tipKey)}
            overlayStyle={tipOverlayStyle}
            overlayInnerStyle={tipInnerStyle}
          >
            <InfoIcon className={cn(size === 'sm' ? 'w-3 h-3' : 'w-4 h-4', 'text-secondary')} />
          </Tooltip>
        )}
      </div>
      <div className={cn(size === 'sm' ? 't-x-sb' : 't-sb', 'text-primary')}>
        {moneyCent === undefined
          ? '-'
          : isBalanceHidden
            ? '∗∗∗∗∗∗'
            : formatPrice(moneyCent ?? 0, false, '')}
      </div>
    </div>
  )
}

interface CreditBreakdownProps {
  bonusStat: BonusStat
  isBalanceHidden?: boolean
  size?: BreakdownSize
}

export const CreditBreakdown = ({
  bonusStat,
  isBalanceHidden,
  size = 'sm',
}: CreditBreakdownProps) => {
  return (
    <div
      className={cn(
        'w-full bg-neutral-50 rounded-lg flex flex-col',
        size === 'sm' ? 'p-3 gap-3' : 'px-3 py-2 gap-1',
      )}
      data-ai-component="credit-breakdown"
    >
      <BreakdownRow
        size={size}
        isBalanceHidden={isBalanceHidden}
        i18nKey="header.creditInfo.rechargeCredits"
        defaultLabel="Recharged Credits"
        moneyCent={bonusStat.rechargeBonus.moneyCent}
      />
      <Divider className="mb-0" />
      <BreakdownRow
        size={size}
        isBalanceHidden={isBalanceHidden}
        i18nKey="header.creditInfo.transferredCredits"
        defaultLabel="Transferred Credits"
        tipKey="header.creditInfo.transferredCreditsTip"
        moneyCent={bonusStat.transferBonus.moneyCent}
      />
      <BreakdownRow
        size={size}
        isBalanceHidden={isBalanceHidden}
        i18nKey="header.creditInfo.otherCredits"
        defaultLabel="Other Credits"
        tipKey="header.creditInfo.otherCreditsTip"
        moneyCent={bonusStat.otherBonus.moneyCent}
      />
      <BreakdownRow
        size={size}
        isBalanceHidden={isBalanceHidden}
        i18nKey="header.creditInfo.giftCredits"
        defaultLabel="Gift Credits"
        tipKey="header.creditInfo.giftCreditsTip"
        moneyCent={bonusStat.giftBonus.moneyCent}
      />
      <Divider className="mb-0" />
      {/* backend does not expose locked credits yet; show a dash placeholder */}
      <BreakdownRow
        size={size}
        isBalanceHidden={isBalanceHidden}
        i18nKey="header.creditInfo.lockedCredits"
        defaultLabel="Locked Credits"
        tipKey="header.creditInfo.lockedCreditsTip"
      />
    </div>
  )
}
